import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Link as LinkIcon, Search } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import ApplyMatchesSkips from '../../dataIngestion/ApplyMatchesSkips';
import { loadScanMatchesForApply, selectionAddsFor } from '../../dataIngestion/applyMatchesResult';
import { applyMatchesInSlices } from '../../dataIngestion/applyMatchesRun';
import { useOrgFeatureFlags } from '../../../hooks/useOrgFeatureFlags';
import { apiService } from '../../../services/api';
import { integrationApiService } from '../../../services/integrationApi';
import type { ApplyMatchSkip, ApplyMatchesResult, IntegrationConfig, ProductMatchResult, SyncMode } from '../../../services/integrationApi';
import { tokens } from '../../../theme/tokens';
import { KpiCard, KpiGrid, SectionCard } from '../../ui';
import UnmatchedListingPlaceholders from '../../placeholders/UnmatchedListingPlaceholders';
import { useShopifyScanJob } from './useShopifyScanJob';
import { listingHoldsKey, useListingMatchesToConfirm } from './useListingMatchesToConfirm';
import type { ListingHold } from './useListingMatchesToConfirm';
import { ListingHoldOpenItem, ListingHoldResolvedItem, lookupFromScan } from './ListingHoldItem';
import type { HoldDecision, ResolvedHold } from './ListingHoldItem';

/** Matches sent per request, so a long list stays under the body limit. */
const APPLY_BATCH = 500;
/** How long a decision can be taken back before the server is told (decisions have no server-side undo). */
export const UNDO_WINDOW_MS = 6000;

export interface ListingMatchesV2ViewProps {
  integration: IntegrationConfig;
  /** Sync mode linked matches are applied under. Defaults to the integration's saved mode. */
  syncMode?: SyncMode;
}

type Match = ProductMatchResult['matched'][number];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Every match of a scan: the inline preview, plus the remaining pages when the list is longer. */
const loadAllMatches = async (integrationId: string, shown: Match[], total: number): Promise<Match[]> => {
  if (shown.length >= total) return shown;
  const rows: Match[] = [];
  for (let page = 0; ; page++) {
    const next = await integrationApiService.getScanMatches(integrationId, page);
    rows.push(...next.rows);
    if (page + 1 >= next.pages) break;
  }
  return rows;
};

/**
 * View C of the Shopify Mapping tab (v2): scan the Shopify catalog read-only,
 * see how many listings link by SKU or barcode, and settle the listings a sync
 * could not decide. Decisions apply at once through the listing-hold endpoints
 * and `applyMatches`, never through the tab's Save bar. A sync still will not
 * create a product while one of its listing holds is unresolved.
 */
const ListingMatchesV2View: React.FC<ListingMatchesV2ViewProps> = ({ integration, syncMode }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { isEnabled } = useOrgFeatureFlags();
  const perIntegrationSelection = isEnabled('per_integration_sync_selection');
  const { result, runKey, job, scanning, running, statusUnreachable, scan, setResult } = useShopifyScanJob(integration);
  const [linking, setLinking] = useState(false);
  const [linkedCount, setLinkedCount] = useState<number | null>(null);
  const [progress, setProgress] = useState<{ phase: 'add' | 'link'; done: number; total: number } | null>(null);
  const [applySkips, setApplySkips] = useState<ApplyMatchesResult['skips']>([]);
  const [updateFailures, setUpdateFailures] = useState<ApplyMatchSkip[]>([]);
  const [matchNames, setMatchNames] = useState<Record<string, string>>({});
  // Rows this session already linked. A later click must not send them again (enrich would rewrite them).
  const [linkedIds, setLinkedIds] = useState<Set<string>>(() => new Set());
  const { t } = useAppTranslation();
  const mode: SyncMode = syncMode ?? integration.settings.syncMode ?? 'link_only';

  useEffect(() => {
    setLinkedCount(null);
    setApplySkips([]);
    setUpdateFailures([]);
    setMatchNames({});
    setLinkedIds(new Set());
  }, [integration.id, runKey]);

  const shown = useMemo(() => result?.matched ?? [], [result]);
  const matchedTotal = Math.max(result?.stats.matchedCount ?? 0, shown.length);
  const previewOnly = shown.length < matchedTotal;
  const newCount = result?.stats.unmatchedMotzuCount ?? result?.unmatchedMotzu.length ?? 0;

  // The scan response is a preview: the SKU / barcode split needs every page when the list is longer.
  const allMatches = useQuery({
    queryKey: ['shopify-scan-all-matches', integration.id, runKey, matchedTotal],
    queryFn: () => loadAllMatches(integration.id, shown, matchedTotal),
    enabled: Boolean(result) && previewOnly,
    staleTime: Infinity,
    retry: 1,
  });
  const counted: Match[] = previewOnly ? allMatches.data ?? [] : shown;
  const bySku = counted.filter((m) => m.matchedBy === 'sku').length;
  const byBarcode = counted.filter((m) => m.matchedBy === 'barcode').length;
  const countsPending = previewOnly && !allMatches.data && !allMatches.isError;

  const holdsInfo = useListingMatchesToConfirm(integration);
  const statusLine = (() => {
    if (scanning) {
      const progress = job?.progress;
      if (job?.status === 'running' && progress && progress.pagesRead > 0) {
        return `Reading page ${progress.pagesRead} · ${progress.productsSeen} products seen`;
      }
      return job?.status === 'queued' ? 'Queued' : 'Starting the scan…';
    }
    if (!result) {
      const flagged = holdsInfo.count;
      return flagged > 0
        ? `Never scanned. The last sync flagged ${plural(flagged, 'possible duplicate', 'possible duplicates')}.`
        : 'Never scanned.';
    }
    const finished = job?.finishedAt ? new Date(job.finishedAt) : null;
    const recent = finished ? Date.now() - finished.getTime() < 2 * 60 * 1000 : false;
    const when = !finished ? 'earlier' : recent ? 'just now' : finished.toLocaleString();
    return `Scanned ${when} · ${plural(result.stats.totalShopify, 'product', 'products')} compared`;
  })();

  const keepsOwnSelection = async (): Promise<boolean> => {
    if (!perIntegrationSelection) return false;
    const rows = await queryClient.fetchQuery({
      queryKey: ['store-sync-selections'],
      queryFn: () => apiService.getStoreSyncSelections(),
      staleTime: 0,
    });
    return rows.some((row) => row.integrationId === integration.id && !row.inheritsFromOrganization);
  };

  /**
   * Links every match now, as the old "Link N matches" button did; the next sync would link them too.
   * `onlyIds` retries the rows that failed. A finished list with nothing left unlinked disables the button.
   */
  const linkMatches = async (onlyIds?: Set<string>) => {
    if (!result) return;
    setLinking(true);
    if (!onlyIds) {
      setApplySkips([]);
      setUpdateFailures([]);
    }
    try {
      let chosen = await loadScanMatchesForApply(integration.id, shown, matchedTotal, integrationApiService.getScanMatches);
      if (onlyIds) chosen = chosen.filter((row) => onlyIds.has(row.canonicalProductId));
      else chosen = chosen.filter((row) => !linkedIds.has(row.canonicalProductId));
      if (chosen.length === 0) return;
      setMatchNames((prev) => {
        const next = { ...prev };
        for (const row of chosen) next[row.canonicalProductId] = row.canonicalName;
        return next;
      });
      // A product of the store's brands joins the sync selection first, like any product the store picked.
      // One a catalog gate excludes is not added: Apply skips it and lists the setting that controls it.
      const toAdd = selectionAddsFor(chosen);
      const added = new Set(toAdd);
      if (toAdd.length > 0) {
        setProgress({ phase: 'add', done: 0, total: toAdd.length });
        const target = (await keepsOwnSelection()) ? integration.id : undefined;
        for (let i = 0; i < toAdd.length; i += APPLY_BATCH) {
          await apiService.addStoreProductsToSync(toAdd.slice(i, i + APPLY_BATCH), target);
          setProgress({ phase: 'add', done: Math.min(i + APPLY_BATCH, toAdd.length), total: toAdd.length });
        }
        queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
        queryClient.invalidateQueries({ queryKey: ['store-sync-selections'] });
        setResult({
          ...result,
          matched: result.matched.map((m) => (added.has(m.canonicalProductId) ? { ...m, inSelection: true } : m)),
          stats: {
            ...result.stats,
            matchedOutsideSelection: Math.max(0, (result.stats.matchedOutsideSelection ?? 0) - added.size),
          },
        });
      }
      const matches = chosen.map((m) => ({ shopifyProductId: m.shopifyProductId, canonicalProductId: m.canonicalProductId }));
      setProgress({ phase: 'link', done: 0, total: matches.length });
      const applied = await applyMatchesInSlices({
        matches,
        apply: (slice) => integrationApiService.applyMatches(integration.id, slice, mode),
        onProgress: (p) => setProgress({ phase: 'link', done: p.done, total: p.total }),
        onSlice: (part, done) => {
          const decided = new Set(matches.slice(0, done).map((row) => row.canonicalProductId));
          const failed = new Set(part.skips.map((skip) => skip.canonicalProductId));
          setLinkedIds((prev) => {
            const next = new Set(prev);
            for (const id of Array.from(decided)) {
              if (failed.has(id)) next.delete(id);
              else next.add(id);
            }
            return next;
          });
          setApplySkips((prev) => [...prev.filter((skip) => !decided.has(skip.canonicalProductId)), ...part.skips]);
          setUpdateFailures((prev) => [
            ...prev.filter((skip) => !decided.has(skip.canonicalProductId)),
            ...(part.updateFailures ?? []),
          ]);
        },
        retryDropped: mode === 'link_only',
      });
      const trouble = applied.skipped > 0 || applied.errors > 0 || (applied.updateFailures?.length ?? 0) > 0;
      // Leave the button available when something still needs a retry. A clean run is done.
      setLinkedCount(trouble ? null : applied.linked + applied.enriched + applied.overridden);
      // A match a catalog gate skipped is named in the toast and listed below with the setting that controls it.
      showToast(
        applied.skipped > 0
          ? t('integrations.catalogScan.appliedWithSkipped', { ...applied })
          : `Matches applied: ${applied.linked} linked, ${applied.enriched} enriched, ${applied.overridden} overridden, ${applied.errors} errors.`,
        trouble ? 'warning' : 'success',
      );
      queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
      queryClient.invalidateQueries({ queryKey: listingHoldsKey(integration.id) });
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not apply the matches.', 'error');
    } finally {
      setLinking(false);
      setProgress(null);
    }
  };

  const progressLabel = progress
    ? progress.phase === 'add'
      ? t('integrations.catalogScan.addingSelection', { done: progress.done, total: progress.total })
      : t('integrations.catalogScan.linking', { done: progress.done, total: progress.total })
    : null;

  const scanButton = (
    <Button
      variant="contained"
      size="small"
      startIcon={scanning ? <CircularProgress size={14} color="inherit" /> : <Search />}
      onClick={scan}
      disabled={scanning}
    >
      {scanning ? 'Scanning…' : result ? 'Scan again' : 'Scan Shopify'}
    </Button>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard
        title="Match your Shopify catalog"
        subtitle="Finds listings already in your store by SKU or barcode, so a sync updates them instead of creating a second one. The scan is read-only."
        actions={scanButton}
        padded
        dense
      >
        <Typography variant="caption" sx={{ color: tokens.ink.faint, fontSize: 12.5 }} data-testid="scan-status-line">
          {statusLine}
        </Typography>
        {statusUnreachable && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Still waiting: the scan status is not coming through right now. It keeps trying.
          </Alert>
        )}
        {!running && job?.status === 'failed' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {job.error || 'The scan failed. Try again.'}
          </Alert>
        )}
        {result?.stats.catalogTruncated && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Your brands have more products than one scan compares. Some products were not checked.
          </Alert>
        )}
      </SectionCard>

      {result && (
        <>
          <KpiGrid minWidth={180}>
            <KpiCard label="Linked by SKU" value={countsPending ? '…' : bySku} />
            <KpiCard label="Linked by barcode" value={countsPending ? '…' : byBarcode} />
            <KpiCard label="New, will be created" value={newCount} />
          </KpiGrid>
          {matchedTotal > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                disabled={linking || linkedCount !== null || linkedIds.size >= matchedTotal}
                aria-busy={linking || undefined}
                startIcon={linking ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                onClick={() => { void linkMatches(); }}
                sx={linking ? { '&.Mui-disabled': { opacity: 1, color: 'text.primary' } } : undefined}
              >
                {linking && progressLabel
                  ? progressLabel
                  : linkedCount !== null || linkedIds.size >= matchedTotal
                    ? 'Matches linked'
                    : `Link ${plural(matchedTotal - linkedIds.size, 'match', 'matches')} now`}
              </Button>
              <Typography variant="caption" color="text.secondary">
                A sync links them too. Linking now applies at once and is not part of Save.
              </Typography>
            </Box>
          )}
          <ApplyMatchesSkips
            integrationId={integration.id}
            skips={applySkips}
            updateFailures={updateFailures}
            names={matchNames}
            onRetry={(skips) => { void linkMatches(new Set(skips.map((skip) => skip.canonicalProductId))); }}
            retrying={linking}
          />
        </>
      )}

      <ToConfirmCard integration={integration} result={result} />

      {result && <UnmatchedListingPlaceholders integrationId={integration.id} listings={result.unmatchedShopify || []} />}
    </Box>
  );
};

interface ToConfirmProps {
  integration: IntegrationConfig;
  result: ProductMatchResult | null;
}

interface Pending {
  decision: HoldDecision;
  hold: ListingHold;
  candidate: { productId: string; title: string } | null;
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * Products the sync would not create because a listing might already be the
 * same item. A decision shows as resolved at once and is sent after a short
 * undo window, because the listing-hold endpoints cannot be reversed once
 * called. Leaving the view sends whatever is still waiting.
 */
const ToConfirmCard: React.FC<ToConfirmProps> = ({ integration, result }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { count } = useListingMatchesToConfirm(integration);
  const holds = useQuery({
    queryKey: listingHoldsKey(integration.id),
    queryFn: () => integrationApiService.listShopifyListingHolds(integration.id),
    staleTime: 30_000,
  });
  const lookup = useMemo(() => lookupFromScan(result), [result]);
  const pending = useRef<Map<string, Pending>>(new Map());
  const [resolved, setResolved] = useState<Record<string, ResolvedHold>>({});

  const commit = useCallback(
    async (entry: Pending) => {
      pending.current.delete(entry.hold.productId);
      setResolved((prev) => (prev[entry.hold.productId] ? { ...prev, [entry.hold.productId]: { ...prev[entry.hold.productId], undoable: false } } : prev));
      try {
        if (entry.decision === 'update' && entry.candidate) {
          await integrationApiService.confirmShopifyListingHold(integration.id, entry.hold.productId, entry.candidate.productId);
        } else {
          await integrationApiService.allowShopifyListingCreate(integration.id, entry.hold.productId);
        }
        queryClient.invalidateQueries({ queryKey: listingHoldsKey(integration.id) });
      } catch (error) {
        // The server did not take it: the item is open again.
        setResolved((prev) => {
          const next = { ...prev };
          delete next[entry.hold.productId];
          return next;
        });
        showToast(error instanceof Error ? error.message : 'Could not save that decision', 'error');
      }
    },
    [integration.id, queryClient, showToast],
  );

  // Leaving the view (or another integration) must not drop a decision the store already made.
  useEffect(() => {
    const waiting = pending.current;
    return () => {
      waiting.forEach((entry) => {
        if (entry.timer) clearTimeout(entry.timer);
        void commit({ ...entry, timer: null });
      });
      waiting.clear();
    };
  }, [commit]);

  const decide = (hold: ListingHold, decision: HoldDecision, candidate: { productId: string; title: string } | null) => {
    const entry: Pending = { hold, decision, candidate, timer: null };
    entry.timer = setTimeout(() => void commit(entry), UNDO_WINDOW_MS);
    pending.current.set(hold.productId, entry);
    setResolved((prev) => ({
      ...prev,
      [hold.productId]: { hold, decision, listingTitle: candidate?.title ?? null, undoable: true },
    }));
  };

  const undo = (productId: string) => {
    const entry = pending.current.get(productId);
    if (entry?.timer) clearTimeout(entry.timer);
    pending.current.delete(productId);
    setResolved((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const open = (holds.data ?? []).filter((h) => !resolved[h.productId]);
  const resolvedItems = Object.values(resolved);

  return (
    <SectionCard
      title="To confirm"
      subtitle="Sync won't create a second Shopify product while one of these might be the same item."
      dense
    >
      {holds.isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ p: '16px 20px' }}>Loading matches…</Typography>
      )}
      {holds.isError && (
        <Typography variant="body2" color="error" sx={{ p: '16px 20px' }}>Could not load listing matches.</Typography>
      )}
      {!holds.isLoading && !holds.isError && count === 0 && resolvedItems.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: '16px 20px' }}>
          Nothing to confirm. When a sync is not sure which Shopify product to update, it shows up here instead of creating another one.
        </Typography>
      )}
      {resolvedItems.map((item) => (
        <ListingHoldResolvedItem key={`done-${item.hold.productId}`} item={item} onUndo={() => undo(item.hold.productId)} />
      ))}
      {open.map((hold) => (
        <ListingHoldOpenItem
          key={hold.productId}
          hold={hold}
          lookup={lookup}
          busy={false}
          onDecide={(decision, candidate) => decide(hold, decision, candidate)}
        />
      ))}
    </SectionCard>
  );
};

export default ListingMatchesV2View;
